<template>
  <div class="admin-container">
    <!-- App Bar -->
    <header class="app-bar">
      <div class="toolbar">
        <button class="sidebar-toggle" @click="toggleSidebar">
          <span class="material-icons">menu</span>
        </button>
        
        <h1 class="app-title">Admin Dashboard</h1>
        
        <div class="spacer"></div>
        
        <!-- User Menu -->
        <div class="user-menu">
          <button class="user-button" @click="toggleUserMenu">
            <div class="user-avatar">
              <span class="material-icons">account_circle</span>
            </div>
          </button>
          
          <div v-if="showUserMenu" class="user-dropdown">
            <div class="dropdown-item" @click="closeUserMenu">
              <span class="material-icons">person</span>
              Meu Perfil
            </div>
            <div class="dropdown-item" @click="closeUserMenu">
              <span class="material-icons">settings</span>
              Configurações
            </div>
            <div class="dropdown-divider"></div>
            <div class="dropdown-item logout" @click="logout">
              <span class="material-icons">logout</span>
              Sair
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Sidebar -->
    <aside :class="['sidebar', { 'collapsed': isSidebarCollapsed }]">
      <div class="sidebar-toolbar">
        <div class="logo-section">
          <img src="@/assets/img/logo.png" alt="LubConsulta" class="logo" />
          <h2 v-if="!isSidebarCollapsed" class="logo-text">LubConsulta</h2>
        </div>
      </div>

      <div class="sidebar-divider"></div>

      <nav class="sidebar-menu">
        <!-- Dashboard -->
        <div class="menu-item active">
          <span class="material-icons">dashboard</span>
          <span class="menu-text">Dashboard</span>
        </div>

        <!-- Users Menu -->
        <div class="menu-group">
          <div class="menu-header" @click="toggleMenu('users')">
            <span class="material-icons">people</span>
            <span class="menu-text">Usuários</span>
            <span class="material-icons menu-arrow">
              {{ openMenus.users ? 'expand_less' : 'expand_more' }}
            </span>
          </div>
          <div v-if="openMenus.users" class="submenu">
            <div class="submenu-item">
              <span class="material-icons">list</span>
              <span class="submenu-text">Todos Usuários</span>
            </div>
            <div class="submenu-item">
              <span class="material-icons">person_add</span>
              <span class="submenu-text">Novo Usuário</span>
            </div>
            <div class="submenu-item">
              <span class="material-icons">groups</span>
              <span class="submenu-text">Grupos</span>
            </div>
          </div>
        </div>

        <!-- Plans Menu -->
        <div class="menu-group">
          <div class="menu-header" @click="toggleMenu('plans')">
            <span class="material-icons">star</span>
            <span class="menu-text">Planos</span>
            <span class="material-icons menu-arrow">
              {{ openMenus.plans ? 'expand_less' : 'expand_more' }}
            </span>
          </div>
          <div v-if="openMenus.plans" class="submenu">
            <div class="submenu-item">
              <span class="material-icons">list</span>
              <span class="submenu-text">Todos Planos</span>
            </div>
            <div class="submenu-item">
              <span class="material-icons">add_circle</span>
              <span class="submenu-text">Novo Plano</span>
            </div>
            <div class="submenu-item">
              <span class="material-icons">subscriptions</span>
              <span class="submenu-text">Assinaturas</span>
            </div>
          </div>
        </div>

        <!-- Reports -->
        <div class="menu-item">
          <span class="material-icons">analytics</span>
          <span class="menu-text">Relatórios</span>
        </div>

        <!-- Settings -->
        <div class="menu-item">
          <span class="material-icons">settings</span>
          <span class="menu-text">Configurações</span>
        </div>
      </nav>
    </aside>

    <!-- Main Content -->
    <main :class="['main-content', { 'expanded': isSidebarCollapsed }]">
      <div class="content-area">
        <!-- Page Header -->
        <div class="page-header">
          <h1 class="page-title">Gerenciamento de Usuários</h1>
          <p class="page-subtitle">Gerencie todos os usuários do sistema</p>
        </div>

        <!-- Filters Section -->
        <div class="filters-section">
          <div class="filters-row">
            <div class="search-field">
              <span class="material-icons search-icon">search</span>
              <input
                v-model="searchTerm"
                type="text"
                placeholder="Buscar usuários..."
                class="search-input"
                @input="filterUsers"
              >
            </div>
            
            <div class="filter-field">
              <select v-model="statusFilter" @change="filterUsers" class="select-input">
                <option value="">Todos os status</option>
                <option value="CONFIRMED">Confirmado</option>
                <option value="UNCONFIRMED">Não confirmado</option>
              </select>
            </div>

            <div class="filter-actions">
              <button class="action-btn outline" @click="resetFilters">
                <span class="material-icons">refresh</span>
                Limpar
              </button>
              
              <button class="action-btn primary" @click="exportToExcel">
                <span class="material-icons">download</span>
                Exportar Excel
              </button>
            </div>
          </div>
        </div>

        <!-- Data Table -->
        <div class="table-container">
          <Vue3Datatable
            :rows="filteredUsers"
            :columns="columns"
            :totalRows="filteredUsers.length"
            :sortable="true"
            :paginate="true"
            :pageSize="10"
            skin="bh-table-striped bh-table-hover"
            class="data-table"
          >
            <!-- Custom slot for status column -->
            <template #status="data">
              <span :class="['status-chip', data.value === 'CONFIRMED' ? 'confirmed' : 'unconfirmed']">
                {{ data.value }}
              </span>
            </template>

            <!-- Custom slot for actions -->
            <template #actions="data">
              <div class="action-buttons">
                <button class="action-icon primary" @click="viewUser(data.value)" title="Visualizar">
                  <span class="material-icons">visibility</span>
                </button>
                
                <button class="action-icon secondary" @click="editUser(data.value)" title="Editar">
                  <span class="material-icons">edit</span>
                </button>
                
                <button class="action-icon danger" @click="deleteUser(data.value)" title="Excluir">
                  <span class="material-icons">delete</span>
                </button>
              </div>
            </template>
          </Vue3Datatable>
        </div>
      </div>
    </main>

    <!-- WhatsApp Fab -->
    <button class="whatsapp-fab" @click="toggleWhatsAppChat">
      <span class="material-icons">chat</span>
    </button>

    <!-- WhatsApp Dialog -->
    <div v-if="showWhatsAppChat" class="dialog-overlay" @click="toggleWhatsAppChat">
      <div class="dialog" @click.stop>
        <div class="dialog-header">
          <h3>Enviar mensagem via WhatsApp</h3>
          <button class="dialog-close" @click="toggleWhatsAppChat">
            <span class="material-icons">close</span>
          </button>
        </div>
        
        <div class="dialog-content">
          <textarea
            v-model="whatsappMessage"
            placeholder="Digite sua mensagem aqui..."
            class="message-textarea"
          ></textarea>
        </div>
        
        <div class="dialog-actions">
          <button class="btn-outline" @click="toggleWhatsAppChat">Cancelar</button>
          <button 
            class="btn-primary" 
            @click="sendWhatsAppMessage" 
            :disabled="!whatsappMessage.trim()"
          >
            <span class="material-icons">send</span>
            Enviar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import Vue3Datatable from '@bhplugin/vue3-datatable'
import '@bhplugin/vue3-datatable/dist/style.css'

export default {
  name: 'AdminDashboard',
  components: {
    Vue3Datatable
  },
  setup() {
    const isSidebarCollapsed = ref(false)
    const showUserMenu = ref(false)
    const openMenus = ref({
      users: true,
      plans: false
    })
    const searchTerm = ref('')
    const statusFilter = ref('')
    const users = ref([])
    const filteredUsers = ref([])
    const showWhatsAppChat = ref(false)
    const whatsappMessage = ref('')

    const columns = ref([
      { field: 'username', title: 'Username', isUnique: true, width: '200px' },
      { 
        field: 'name', 
        title: 'Nome', 
        width: '250px',
        value: (row) => row.attributes?.name || 'N/A'
      },
      { 
        field: 'email', 
        title: 'Email', 
        width: '250px',
        value: (row) => row.attributes?.email || 'N/A'
      },
      { 
        field: 'userStatus', 
        title: 'Status', 
        width: '120px',
        type: 'status'
      },
      { 
        field: 'plano', 
        title: 'Plano', 
        width: '150px',
        value: (row) => row.attributes?.['custom:plano'] || 'N/A'
      },
      { 
        field: 'userCreateDate', 
        title: 'Data Criação', 
        width: '150px',
        value: (row) => new Date(row.userCreateDate).toLocaleDateString('pt-BR')
      },
      { 
        field: 'username', 
        title: 'Ações', 
        width: '150px',
        type: 'actions',
        sort: false
      }
    ])

    const toggleSidebar = () => {
      isSidebarCollapsed.value = !isSidebarCollapsed.value
    }

    const toggleUserMenu = () => {
      showUserMenu.value = !showUserMenu.value
    }

    const closeUserMenu = () => {
      showUserMenu.value = false
    }

    const toggleMenu = (menu) => {
      openMenus.value[menu] = !openMenus.value[menu]
    }

    const fetchUsers = async () => {
      try {
        // Simulação de chamada API
        // const response = await this.$http.get('/cognito/users')
        // users.value = response.data
        // filteredUsers.value = [...users.value]
        
        // Dados mockados para demonstração
        users.value = [
          {
            username: 'user1',
            userStatus: 'CONFIRMED',
            userCreateDate: new Date().toISOString(),
            attributes: {
              name: 'João Silva',
              email: 'joao@email.com',
              'custom:plano': 'Premium'
            }
          },
          {
            username: 'user2',
            userStatus: 'UNCONFIRMED',
            userCreateDate: new Date().toISOString(),
            attributes: {
              name: 'Maria Santos',
              email: 'maria@email.com',
              'custom:plano': 'Básico'
            }
          },
          {
            username: 'user3',
            userStatus: 'CONFIRMED',
            userCreateDate: new Date().toISOString(),
            attributes: {
              name: 'Pedro Oliveira',
              email: 'pedro@email.com',
              'custom:plano': 'Enterprise'
            }
          }
        ]
        filteredUsers.value = [...users.value]
      } catch (error) {
        console.error('Erro ao buscar usuários:', error)
      }
    }

    const filterUsers = () => {
      filteredUsers.value = users.value.filter(user => {
        const matchesSearch = !searchTerm.value || 
          user.username.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
          (user.attributes?.name && user.attributes.name.toLowerCase().includes(searchTerm.value.toLowerCase())) ||
          (user.attributes?.email && user.attributes.email.toLowerCase().includes(searchTerm.value.toLowerCase()))
        
        const matchesStatus = !statusFilter.value || user.userStatus === statusFilter.value
        
        return matchesSearch && matchesStatus
      })
    }

    const resetFilters = () => {
      searchTerm.value = ''
      statusFilter.value = ''
      filteredUsers.value = [...users.value]
    }

    const exportToExcel = () => {
      console.log('Exportando para Excel...')
    }

    const viewUser = (userId) => {
      console.log('Visualizar usuário:', userId)
    }

    const editUser = (userId) => {
      console.log('Editar usuário:', userId)
    }

    const deleteUser = (userId) => {
      if (confirm('Tem certeza que deseja excluir este usuário?')) {
        console.log('Excluir usuário:', userId)
      }
    }

    const logout = () => {
      console.log('Logout realizado')
    }

    const toggleWhatsAppChat = () => {
      showWhatsAppChat.value = !showWhatsAppChat.value
    }

    const sendWhatsAppMessage = () => {
      if (whatsappMessage.value.trim()) {
        const phoneNumber = '5585992282002'
        const encodedMessage = encodeURIComponent(whatsappMessage.value)
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`
        
        window.open(whatsappUrl, '_blank')
        whatsappMessage.value = ''
        showWhatsAppChat.value = false
      }
    }

    onMounted(() => {
      fetchUsers()
    })

    return {
      isSidebarCollapsed,
      showUserMenu,
      openMenus,
      searchTerm,
      statusFilter,
      users,
      filteredUsers,
      showWhatsAppChat,
      whatsappMessage,
      columns,
      toggleSidebar,
      toggleUserMenu,
      closeUserMenu,
      toggleMenu,
      filterUsers,
      resetFilters,
      exportToExcel,
      viewUser,
      editUser,
      deleteUser,
      logout,
      toggleWhatsAppChat,
      sendWhatsAppMessage
    }
  }
}
</script>

<style scoped>
/* Material Icons */
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

.admin-container {
  display: flex;
  min-height: 100vh;
  background-color: #f5f7fa;
  font-family: 'Roboto', sans-serif;
}

/* App Bar */
.app-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  z-index: 1000;
}

.toolbar {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 16px;
}

.sidebar-toggle {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 50%;
  margin-right: 16px;
  color: #666;
}

.sidebar-toggle:hover {
  background-color: rgba(0,0,0,0.04);
}

.app-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.spacer {
  flex-grow: 1;
}

.user-menu {
  position: relative;
}

.user-button {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 50%;
  color: #666;
}

.user-button:hover {
  background-color: rgba(0,0,0,0.04);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #1976d2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  min-width: 200px;
  padding: 8px 0;
  z-index: 1001;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  color: #333;
  font-size: 0.875rem;
}

.dropdown-item:hover {
  background-color: rgba(0,0,0,0.04);
}

.dropdown-item .material-icons {
  margin-right: 12px;
  font-size: 18px;
}

.dropdown-divider {
  height: 1px;
  background-color: rgba(0,0,0,0.12);
  margin: 8px 0;
}

.dropdown-item.logout {
  color: #d32f2f;
}

/* Sidebar */
.sidebar {
  position: fixed;
  top: 64px;
  left: 0;
  width: 280px;
  height: calc(100vh - 64px);
  background: white;
  box-shadow: 1px 0 3px rgba(0,0,0,0.1);
  transition: width 0.3s ease;
  z-index: 999;
  overflow-y: auto;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-toolbar {
  padding: 16px;
  border-bottom: 1px solid rgba(0,0,0,0.12);
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.logo-text {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  white-space: nowrap;
}

.sidebar.collapsed .logo-text {
  display: none;
}

.sidebar-divider {
  height: 1px;
  background-color: rgba(0,0,0,0.12);
}

.sidebar-menu {
  padding: 8px 0;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin: 4px 12px;
  border-radius: 8px;
  cursor: pointer;
  color: #333;
  transition: background-color 0.2s;
}

.menu-item:hover {
  background-color: rgba(0,0,0,0.04);
}

.menu-item.active {
  background-color: rgba(25, 118, 210, 0.08);
  color: #1976d2;
}

.menu-item .material-icons {
  margin-right: 12px;
  font-size: 20px;
}

.menu-text {
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}

.sidebar.collapsed .menu-text {
  display: none;
}

.menu-group {
  margin-bottom: 4px;
}

.menu-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin: 4px 12px;
  border-radius: 8px;
  cursor: pointer;
  color: #333;
  transition: background-color 0.2s;
}

.menu-header:hover {
  background-color: rgba(0,0,0,0.04);
}

.menu-arrow {
  margin-left: auto;
  transition: transform 0.2s;
}

.submenu {
  background-color: rgba(0,0,0,0.02);
  margin: 4px 12px;
  border-radius: 8px;
  overflow: hidden;
}

.submenu-item {
  display: flex;
  align-items: center;
  padding: 8px 16px 8px 48px;
  cursor: pointer;
  color: #666;
  font-size: 0.875rem;
  transition: background-color 0.2s;
}

.submenu-item:hover {
  background-color: rgba(0,0,0,0.04);
}

.submenu-item .material-icons {
  margin-right: 12px;
  font-size: 16px;
}

.submenu-text {
  white-space: nowrap;
}

/* Main Content */
.main-content {
  flex: 1;
  margin-left: 280px;
  margin-top: 64px;
  transition: margin-left 0.3s ease;
  min-height: calc(100vh - 64px);
}

.main-content.expanded {
  margin-left: 70px;
}

.content-area {
  padding: 24px;
  flex: 1;
}

/* Page Header */
.page-header {
  margin-bottom: 24px;
  padding: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
}

/* Filters Section */
.filters-section {
  background: white;
  padding: 20px;
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.filters-row {
  display: flex;
  gap: 16px;
  align-items: end;
  flex-wrap: wrap;
}

.search-field {
  flex: 1;
  min-width: 300px;
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 40px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1);
}

.filter-field {
  min-width: 200px;
}

.select-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
}

.select-input:focus {
  outline: none;
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1);
}

.filter-actions {
  display: flex;
  gap: 12px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn.primary {
  background: #1976d2;
  color: white;
}

.action-btn.primary:hover {
  background: #1565c0;
}

.action-btn.outline {
  background: transparent;
  border: 1px solid #1976d2;
  color: #1976d2;
}

.action-btn.outline:hover {
  background: rgba(25, 118, 210, 0.04);
}

/* Table Container */
.table-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
}

.data-table {
  --bh-border-radius: 12px;
}

:deep(.bh-table-striped) {
  border-radius: 12px;
}

:deep(.bh-table-hover tbody tr:hover) {
  background-color: rgba(25, 118, 210, 0.04) !important;
}

/* Status Chip */
.status-chip {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-chip.confirmed {
  background: #e8f5e8;
  color: #2e7d32;
}

.status-chip.unconfirmed {
  background: #fff3e0;
  color: #ef6c00;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 4px;
  justify-content: center;
}

.action-icon {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  font-size: 16px;
}

.action-icon.primary {
  background: #e3f2fd;
  color: #1976d2;
}

.action-icon.primary:hover {
  background: #bbdefb;
}

.action-icon.secondary {
  background: #e8f5e8;
  color: #2e7d32;
}

.action-icon.secondary:hover {
  background: #c8e6c9;
}

.action-icon.danger {
  background: #ffebee;
  color: #c62828;
}

.action-icon.danger:hover {
  background: #ffcdd2;
}

:deep(.bh-pagination) {
  padding: 16px !important;
}

/* WhatsApp FAB */
.whatsapp-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #25D366, #128C7E);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  transition: transform 0.2s;
}

.whatsapp-fab:hover {
  transform: scale(1.1);
}

/* Dialog */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.dialog {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.3);
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0,0,0,0.12);
}

.dialog-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.dialog-close {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 50%;
  color: #666;
}

.dialog-close:hover {
  background: rgba(0,0,0,0.04);
}

.dialog-content {
  padding: 20px 24px;
}

.message-textarea {
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
}

.message-textarea:focus {
  outline: none;
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1);
}

.dialog-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid rgba(0,0,0,0.12);
}

.btn-outline, .btn-primary {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-outline {
  background: transparent;
  border: 1px solid #1976d2;
  color: #1976d2;
}

.btn-outline:hover {
  background: rgba(25, 118, 210, 0.04);
}

.btn-primary {
  background: #1976d2;
  color: white;
}

.btn-primary:hover {
  background: #1565c0;
}

.btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }
  
  .sidebar.mobile-open {
    transform: translateX(0);
  }
  
  .main-content {
    margin-left: 0;
  }
  
  .content-area {
    padding: 16px;
  }
  
  .filters-row {
    flex-direction: column;
  }
  
  .search-field,
  .filter-field {
    min-width: 100%;
  }
  
  .filter-actions {
    width: 100%;
    justify-content: stretch;
  }
  
  .action-btn {
    flex: 1;
    justify-content: center;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .app-title {
    display: none;
  }
  
  .dialog {
    width: 95%;
    margin: 20px;
  }
  
  .dialog-actions {
    flex-direction: column;
  }
}
</style>